import { Loader } from "@googlemaps/js-api-loader";
import { notEmpty } from "../../Shared/Utilities/filters";

export type MapDefinition = {
  bounds: google.maps.LatLngBounds;
  currentPositionMarker?: google.maps.marker.AdvancedMarkerElement;
  map: google.maps.Map;
  markers: google.maps.marker.AdvancedMarkerElement[];
};

export type GeoLocation = {
  latitude: number;
  longitude: number;
};

const defaultBoundsPadding: google.maps.Padding = {
  top: 60,
  right: 30,
  bottom: 30,
  left: 30
};

const maps = new Map<string, MapDefinition>();

const updateBounds = (mapId: string, fitTo: "closest" | "all") => {
  const mapDefinition = maps.get(mapId);

  if (!mapDefinition) {
    throw `mapId "${mapId}" does not exist`;
  }

  const { currentPositionMarker, map, markers } = mapDefinition;

  const center = currentPositionMarker?.position ?? map.getCenter();

  if (center) {
    let fitToLocations: (google.maps.LatLng | google.maps.LatLngLiteral)[] = [];

    let padding: google.maps.Padding;

    if (fitTo === "closest") {
      const sortedMarkerPositions: google.maps.LatLngLiteral[] = markers
        .map(marker => {
          if (marker.position) {
            return marker.position as google.maps.LatLngLiteral;
          }
        })
        .filter(notEmpty);

      sortedMarkerPositions.sort((a, b) => {
        const aDistance = google.maps.geometry.spherical.computeDistanceBetween(
          center,
          a
        );
        const bDistance = google.maps.geometry.spherical.computeDistanceBetween(
          center,
          b
        );

        if (aDistance === bDistance) {
          return 0;
        }

        const result = aDistance < bDistance ? -1 : 1;

        return result;
      });

      const firstSortedMarkerPosition = sortedMarkerPositions[0];

      if (firstSortedMarkerPosition) {
        fitToLocations = [firstSortedMarkerPosition];
      }

      // Increase padding when zooming in on two markers
      padding = {
        top: 120,
        right: 50,
        bottom: 80,
        left: 50
      };
    } else {
      fitToLocations = markers.map(marker => marker.position).filter(notEmpty);

      padding = defaultBoundsPadding;
    }

    mapDefinition.bounds = new google.maps.LatLngBounds(center);

    for (const location of fitToLocations) {
      mapDefinition.bounds.extend(location);
    }

    map.fitBounds(mapDefinition.bounds, padding);
  }
};

export const loadGoogleMaps = async (apiKey: string, language?: string) => {
  const loader = new Loader({
    apiKey,
    version: "weekly",
    libraries: ["geometry", "places"],
    language: language
  });

  await loader.load();
};

export const initializeMap = async (
  element: HTMLElement,
  mapId: string,
  defaultLat = 0.0,
  defaultLng = 0.0,
  defaultZoom = 5
) => {
  const latLng = new google.maps.LatLng(defaultLat, defaultLng);

  const { Map } = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary;

  const map = new Map(element, {
    center: latLng,
    controlSize: 24,
    disableDefaultUI: true,
    mapId,
    zoom: defaultZoom,
    zoomControl: true,
    zoomControlOptions: {
      position: google.maps.ControlPosition.RIGHT_BOTTOM
    }
  });

  maps.set(mapId, {
    bounds: new google.maps.LatLngBounds(),
    map,
    markers: []
  });

  return map;
};

export const getCurrentPosition = () =>
  new Promise<GeoLocation>((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        userPosition =>
          // Note: need to map properties from coords onto a new object here,
          // since the properties are marked as readonly.
          resolve({
            latitude: userPosition.coords.latitude,
            longitude: userPosition.coords.longitude
          }),
        error => {
          if (error.PERMISSION_DENIED) {
            console.warn("Access to user location has been denied");
          } else if (error.TIMEOUT) {
            console.error("Timeout when fetching user location");
          } else if (error.POSITION_UNAVAILABLE) {
            console.error("User location is unavailable");
          } else {
            console.error(
              `Unhandled exception when fetching user location: ${error.code}`,
              error.message
            );
          }

          reject(`[Error code ${error.code}] ${error.message}`);
        }
      );
    } else {
      const error = "Geolocation not supported";
      console.error(error);
      reject(error);
    }
  });

export const updateCurrentPositionPin = async (
  mapId: string,
  currentPosition: google.maps.LatLngLiteral
) => {
  const mapDefinition = maps.get(mapId);

  if (!mapDefinition) {
    throw `mapId "${mapId}" does not exist`;
  }

  const { currentPositionMarker, map } = mapDefinition;

  if (currentPositionMarker) {
    currentPositionMarker.position = currentPosition;
  } else {
    const { AdvancedMarkerElement } = (await google.maps.importLibrary(
      "marker"
    )) as google.maps.MarkerLibrary;

    const marker = new AdvancedMarkerElement({
      content: document.getElementById("user-position-marker"),
      map,
      position: currentPosition,
      // TODO: Translate
      title: "Current location"
    });

    mapDefinition.currentPositionMarker = marker;
  }

  updateBounds(mapId, "closest");
};

export const clearMapMarkers = (mapId: string) => {
  const mapDefinition = maps.get(mapId);

  if (!mapDefinition) {
    throw `mapId "${mapId}" does not exist`;
  }

  for (const marker of mapDefinition.markers) {
    if (marker.content) {
      // Clear content id so it is not confused with new content returned by HTMX
      (marker.content as HTMLElement).id = "";
    }

    // Setting map to null removes the marker from the Google maps widget
    marker.map = null;
  }

  mapDefinition.markers = [];
};

/**
 * Render markers on a map.
 * @param mapId Unique identifier of the map.
 * @param newMarkers Markers to render.
 * @param fitToMarkers (Optional, default: true) Fit map to the markers once they have been added.
 */
export const addMapMarkers = async (
  mapId: string,
  newMarkers: google.maps.marker.AdvancedMarkerElementOptions[],
  fitToMarkers: boolean = true
) => {
  const mapDefinition = maps.get(mapId);

  if (!mapDefinition) {
    throw `mapId "${mapId}" does not exist`;
  }

  const { map } = mapDefinition;

  const { AdvancedMarkerElement } = (await google.maps.importLibrary(
    "marker"
  )) as google.maps.MarkerLibrary;

  for (const newMarker of newMarkers) {
    const marker = new AdvancedMarkerElement({
      ...newMarker,
      map
    });

    marker.addListener("click", () => {
      if (marker.position) {
        map.panTo(marker.position);
      }
    });

    mapDefinition.markers.push(marker);
  }

  if (fitToMarkers) {
    updateBounds(mapId, "all");
  }
};
